import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API, setSessionToken } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Mail, Lock, ArrowRight, Sparkles, AlertCircle } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import { toast } from 'sonner';

const REDIRECT_URL = `${window.location.origin}/dashboard`;
const AUTH_URL = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(REDIRECT_URL)}`;

function formatErr(detail) {
  if (!detail) return 'Login failed. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function Login() {
  const { user, checkAuth } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate(user.must_change_password ? '/change-password' : '/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/auth/login`,
        // Bug fix Feb-17: trim outer whitespace from BOTH email and password.
        // Users pasting a temp password from email/WhatsApp often unknowingly
        // include a leading/trailing space, which flipped login into "invalid
        // password" on production. Real passwords with intentional outer
        // whitespace are vanishingly rare — safe to trim.
        { email: email.trim().toLowerCase(), password: password.trim() },
        { withCredentials: true }
      );
      toast.success(`Welcome, ${r.data.user.name}`);
      await checkAuth();
      if (r.data.must_change_password) {
        navigate('/change-password');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const msg = formatErr(err.response?.data?.detail) || err.message;
      // Phase 37.3 — friendlier hint when a client evaluator tries to log in
      // with the trial accounts on a fresh environment where they weren't
      // seeded yet.
      const looksLikeTrial = /trial\d?-/i.test(email);
      const isInvalid = err.response?.status === 401 || /invalid email or password/i.test(msg);
      if (looksLikeTrial && isInvalid) {
        setError(
          `${msg} — If this is a fresh deployment, ask your Super Admin to click ` +
          `"Seed Trial Accounts" on the Super Admin dashboard first, then retry.`
        );
      } else {
        setError(msg);
      }
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-white flex flex-col">
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03] z-0"
        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")' }}
      />
      <header className="relative z-30 backdrop-blur-xl bg-[#0a0f1e]/70 border-b border-teal-900/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <BrandLogo variant="icon" size={40} className="rounded-xl ring-1 ring-teal-500/30" />
            <div className="leading-tight">
              <div className="text-lg font-bold">FM Flexi Pro<span className="text-teal-400 text-xs align-top ml-0.5">™</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">by SKG Apps</div>
            </div>
          </Link>
          <Link to="/" className="text-sm text-slate-300 hover:text-teal-400">← Back</Link>
        </div>
      </header>

      <main className="flex-1 relative z-10 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 mb-4">
              <Sparkles className="w-3.5 h-3.5 text-teal-400" />
              <span className="text-xs font-medium text-teal-300">SECURE LOGIN</span>
            </div>
            <h1 className="text-3xl font-bold">Sign in to your workspace</h1>
            <p className="text-sm text-slate-400 mt-2">Use the credentials provided by your administrator.</p>
          </div>

          <Card className="bg-slate-900/60 border-teal-900/40 backdrop-blur p-6 space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="text-slate-200">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="pl-10 bg-slate-950 border-slate-700 text-white"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-200">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your temporary or current password"
                    className="pl-10 bg-slate-950 border-slate-700 text-white"
                    required
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">If this is your first login, you&apos;ll be prompted to set a new password.</p>
              </div>

              {error && (
                <div data-testid="login-error" className="flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                data-testid="login-submit-btn"
                type="submit"
                disabled={submitting || !email || !password}
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold"
              >
                {submitting ? 'Signing in...' : (<>Sign In<ArrowRight className="w-4 h-4 ml-1" /></>)}
              </Button>
            </form>

            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-700/60"></div></div>
              <div className="relative flex justify-center text-[11px]"><span className="px-2 bg-slate-900/60 text-slate-500 uppercase tracking-wider">or</span></div>
            </div>

            <a
              href={AUTH_URL}
              data-testid="login-google-btn"
              className="block w-full text-center px-4 py-2.5 rounded-md border border-slate-700 hover:border-teal-500 text-slate-200 hover:text-teal-300 text-sm font-medium transition"
            >
              Continue with Google
            </a>
            <p className="text-[11px] text-amber-300/90 mt-1 leading-snug">
              Google sign-in temporarily unstable for some browsers. If it fails,{' '}
              <Link to="/signup" className="underline font-semibold">create an account with email →</Link>
            </p>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6">
            New to FM Flexi Pro? <Link to="/signup" className="text-teal-400 hover:underline font-semibold">Create your workspace →</Link>
            <br />
            <span className="text-slate-500">Existing user? Your company&apos;s User Admin can invite you.</span>
            <br />
            <Link to="/contact" className="text-teal-400 hover:underline">Need help? Contact us →</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
